import React, {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
  useState
} from "react";
import {
  Grid,
  Box,
  FormControl,
  FormControlLabel,
  RadioGroup,
  Radio,
  Checkbox,
  Typography,
  Collapse,
  ButtonBase
} from "@material-ui/core";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import LabeledTextField from "../shared/ui/HaklaiLabeledTextField";
import CustomLink from "../../../layout/CustomLink";
import HelpDialog from "../../../layout/HelpDialog";
import haklaiHelpTexts from "../shared/haklaiHelpTexts";
import HasRabinovichDialog from "../../../shared/dialogs/HasRabinovichDialog";
import {
  COMPACT_CURRENCY_LABELS,
  MAX_MONEY_AMOUNT,
  getHaklaiCurrencyOptions,
  RABINOVICH_WINDOW_START_EXCLUSIVE,
  RABINOVICH_WINDOW_END_EXCLUSIVE
} from "../shared/haklaiConstants";
import useHaklaiPreCalcStyles, {
  PRECALC_LABEL_WIDTHS,
  PRECALC_INPUT_WIDTHS
} from "./haklaiPreCalcStyles";
import {
  getHaklaiAcquisitionDetailsConfig,
  getHaklaiAcquisitionDisplayDate
} from "./logic/haklaiPreCalcHelpers";
import {
  HAKLAI_PRE_CALC_ERRORS,
  HAKLAI_PRE_CALC_LABELS
} from "./haklaiPreCalcTexts";
import moment from "moment";

/**
 * Owns the opening Haklai input section.
 *
 * Responsibilities:
 * - collects sale, acquisition, parcel, and exemption inputs
 * - reveals acquisition-specific fields based on the selected flow
 * - coordinates local field scrolling and prerequisite validation display
 *
 * Pure pre-calc date and option rules belong in `logic/`, while dialog UI
 * should stay under `components/`.
 */

// Reserve one caption-line under each input even when it is valid, so an
// invalid message does not grow the section (and then collapse it again
// when the message clears). A non-breaking space keeps that line at a
// stable height. The same pixel value is the label's marginBottom, so the
// label stays aligned with the input rather than the helper line.
const HELPER_TEXT_PLACEHOLDER = "\u00A0";
const FIELD_HELPER_SLOT_PX = 16;
const PRE_CALC_SECTION_TRANSITION_MS = 180;
const WAY_OF_GETTING_PROPERTY_OPTIONS = Object.freeze([
  { value: "purchase", label: HAKLAI_PRE_CALC_LABELS.purchase },
  { value: "inheritance", label: HAKLAI_PRE_CALC_LABELS.inheritance },
  { value: "gift", label: HAKLAI_PRE_CALC_LABELS.gift }
]);
const BASIC_INFO_REVEAL_FIELDS = new Set(["soldPropertyDate", "totalPropertyCost"]);
const ACQUISITION_REVEAL_FIELDS = new Set([
  "wayOfGettingProperty",
  "deduDate",
  "testatorPassingDate",
  "testatorBoughtPropertyDate",
  "amount"
]);

const afterNextPaint = (callback) => {
  if (typeof window === "undefined" || typeof window.requestAnimationFrame !== "function") {
    callback();
    return;
  }
  window.requestAnimationFrame(() => window.requestAnimationFrame(callback));
};

const scrollToNamedField = (rootElement, fieldName) => {
  if (!rootElement || !fieldName) return false;
  const fieldElement = rootElement.querySelector(`[name="${fieldName}"]`);
  if (!fieldElement) return false;
  const scrollTarget =
    fieldElement.closest(".MuiFormControl-root")
    || fieldElement.closest(".MuiGrid-root")
    || fieldElement;

  scrollTarget.scrollIntoView({
    behavior: "smooth",
    block: "center",
    inline: "nearest"
  });

  if (typeof fieldElement.focus === "function") {
    try {
      fieldElement.focus({ preventScroll: true });
    } catch (error) {
      fieldElement.focus();
    }
  }

  return true;
};

// Blur the clicked link *and* the currently-focused element before opening a
// dialog. Needed because dialogs returning focus to a previously-focused
// input would re-open the keyboard / re-trigger blur-validation on every open.
const blurFocusedElement = (event) => {
  const currentTarget = event?.currentTarget;
  if (currentTarget && typeof currentTarget.blur === "function") {
    currentTarget.blur();
  }

  const activeElement = typeof document !== "undefined" ? document.activeElement : null;
  if (activeElement && activeElement !== currentTarget && typeof activeElement.blur === "function") {
    activeElement.blur();
  }
};

const BasicInfoField = ({
  classes,
  label,
  helpDialog,
  labelWidth,
  alignLabelToInput = false,
  fieldClassName = "",
  belowMainRow = null,
  children
}) => {
  const mainRow = (
    <Box className={classes.basicInfoField}>
      <Box
        className={classes.basicInfoFieldHeader}
        style={{
          ...(labelWidth ? { minWidth: labelWidth } : {}),
          ...(alignLabelToInput ? { marginBottom: FIELD_HELPER_SLOT_PX } : {})
        }}
      >
        <Typography variant="subtitle1" className={classes.basicInfoFieldLabel}>
          {label}
        </Typography>
        {helpDialog ? <HelpDialog helpDialog={helpDialog} label={label} /> : null}
      </Box>
      <Box className={classes.basicInfoFieldControl}>{children}</Box>
    </Box>
  );

  return (
    <Grid item xs={12} sm="auto" className={fieldClassName}>
      {belowMainRow ? (
        <Box className={classes.basicInfoFieldStack}>
          {mainRow}
          <Box
            className={classes.basicInfoFieldBelowMainRow}
            style={{
              ...(labelWidth != null ? { marginInlineStart: labelWidth + 6 } : {})
            }}
          >
            {belowMainRow}
          </Box>
        </Box>
      ) : (
        mainRow
      )}
    </Grid>
  );
};

const InlineQuestionField = ({ classes, label, helpDialog, hint, errorText, children }) => {
  // The feedback caption (hint or error) is rendered unconditionally so the
  // vertical space it occupies is reserved from first paint. Without this,
  // the hint-to-error transition (or the hint-disappears-on-first-selection
  // moment) would collapse the caption line and shift every block below
  // upward. Error takes precedence over hint; when neither is present, a
  // non-breaking space keeps the line alive invisibly.
  const feedbackMessage = errorText || hint || HELPER_TEXT_PLACEHOLDER;
  const feedbackClass = errorText ? classes.inlineQuestionError : classes.inlineQuestionHint;
  return (
    <FormControl component="fieldset">
      <Box className={classes.inlineQuestionField}>
        <Typography variant="subtitle1" className={classes.inlineQuestionLabel}>
          {label}
        </Typography>
        {helpDialog ? <HelpDialog helpDialog={helpDialog} label={label} /> : null}
        {children}
      </Box>
      <Typography variant="caption" className={feedbackClass}>
        {feedbackMessage}
      </Typography>
    </FormControl>
  );
};

const HaklaiPreCalcSection = React.memo(forwardRef((props, ref) => {
  const classes = useHaklaiPreCalcStyles();
  const sectionRootRef = useRef(null);
  const [hasRabinovichDialogOpen, setHasRabinovichDialogOpen] = useState(false);
  const handleAcquisitionDetailsOnChange = props.handleAcquisitionDetailsOnChange;
  const acquisitionContext = useMemo(() => ({
    wayOfGettingProperty: props.wayOfGettingProperty,
    acquisitionDetails: props.acquisitionDetails || {}
  }), [props.acquisitionDetails, props.wayOfGettingProperty]);
  const acquisitionDisplayDate = useMemo(
    () => getHaklaiAcquisitionDisplayDate(acquisitionContext),
    [acquisitionContext]
  );
  const acquisitionDetailsConfig = useMemo(
    () => (
      props.wayOfGettingProperty
        ? getHaklaiAcquisitionDetailsConfig(acquisitionContext)
        : null
    ),
    [acquisitionContext, props.wayOfGettingProperty]
  );
  const acquisitionCurrencyOptions = useMemo(
    () => getHaklaiCurrencyOptions(acquisitionDisplayDate).map((option) => ({
      ...option,
      label: COMPACT_CURRENCY_LABELS[option.value] || option.label
    })),
    [acquisitionDisplayDate]
  );
  const fieldErrors = props.fieldErrors || {};
  const handlePrerequisiteFieldBlur = props.handlePrerequisiteFieldBlur || (() => {});
  const [basicInfoExpanded, setBasicInfoExpanded] = useState(true);
  const [acquisitionExpanded, setAcquisitionExpanded] = useState(true);

  const revealField = useCallback((fieldName) => {
    if (!fieldName) return false;
    if (BASIC_INFO_REVEAL_FIELDS.has(fieldName)) setBasicInfoExpanded(true);
    if (ACQUISITION_REVEAL_FIELDS.has(fieldName)) setAcquisitionExpanded(true);
    afterNextPaint(() => scrollToNamedField(sectionRootRef.current, fieldName));
    return true;
  }, []);

  useImperativeHandle(ref, () => ({
    revealField
  }), [revealField]);

  // Check if Rabinovich checkbox should be shown
  const shouldShowRabinovich = useMemo(() => {
    if (!props.wayOfGettingProperty) return false;
    let dateToCheck;
    if (props.wayOfGettingProperty === "purchase" || props.wayOfGettingProperty === "gift") {
      dateToCheck = props.acquisitionDetails?.deduDate;
    } else if (props.wayOfGettingProperty === "inheritance") {
      dateToCheck = props.acquisitionDetails?.testatorBoughtPropertyDate;
    }
    if (!dateToCheck) return false;
    const date = moment(dateToCheck);
    if (!date.isValid()) return false;
    return (
      date.isBefore(moment(RABINOVICH_WINDOW_END_EXCLUSIVE), "day") &&
      date.isAfter(moment(RABINOVICH_WINDOW_START_EXCLUSIVE), "day")
    );
  }, [
    props.acquisitionDetails?.deduDate,
    props.acquisitionDetails?.testatorBoughtPropertyDate,
    props.wayOfGettingProperty
  ]);

  const renderAcquisitionDivider = (key) => (
    <Grid
      key={key}
      item
      xs={12}
      md="auto"
      className={`${classes.stretchGridItem} ${classes.acquisitionFieldDividerItem}`}
    >
      <Box className={classes.fieldDivider} />
    </Grid>
  );

  const renderAmountCurrencyFields = (amountLabel, currencyOpts) => {
    const amountHelperText = fieldErrors.amount || HELPER_TEXT_PLACEHOLDER;
    const amountHelperClassName = fieldErrors.amount ? classes.amountFieldError : undefined;
    // Top-alignment pairs the amount input with the currency select. The
    // amount always reserves a helper line; the currency select does not.
    return (
      <Grid item xs={12} sm="auto">
        <Grid container direction="row" spacing={1} alignItems="flex-start" wrap="wrap">
          <Grid item xs="auto">
            <LabeledTextField
              type="number"
              label={amountLabel}
              name="amount"
              textFieldWidth={PRECALC_INPUT_WIDTHS.money}
              max={MAX_MONEY_AMOUNT}
              onChange={props.handleAcquisitionDetailsOnChange}
              value={props.acquisitionDetails?.amount || ""}
              helpDialog={haklaiHelpTexts.purchaseAmountInOriginalCurrency}
              error={Boolean(fieldErrors.amount)}
              helperText={amountHelperText}
              helperTextClassName={amountHelperClassName}
              helperTextMinHeight={FIELD_HELPER_SLOT_PX}
              pinLabelToInput
              stackLabelOnXs
              onBlur={() => handlePrerequisiteFieldBlur("amount")}
            />
          </Grid>
          <Grid item xs="auto" className="haklai-currency-field">
            <LabeledTextField
              type="select"
              name="deduCurrency"
              textFieldWidth={PRECALC_INPUT_WIDTHS.currency}
              onChange={props.handleAcquisitionDetailsOnChange}
              value={props.acquisitionDetails?.deduCurrency || "newShekel"}
              options={currencyOpts}
              openMenuBelow
            />
          </Grid>
        </Grid>
      </Grid>
    );
  };

  const renderAcquisitionDetails = () => {
    if (!props.wayOfGettingProperty) return null;

    if (!acquisitionDetailsConfig) return null;
    // Dates and the amount share one row. The amount group stays together
    // and wraps under the dates only when the row is not wide enough, which
    // is the inheritance case with two dates.
    return (
      <Grid container direction="row" spacing={1} alignItems="flex-start" wrap="wrap">
        {acquisitionDetailsConfig.dateFields.map((field, index) => (
          <React.Fragment key={field.key}>
            {index > 0 ? renderAcquisitionDivider(`${field.key}-divider`) : null}
            <Grid item xs={12} sm="auto">
              <LabeledTextField
                type="date"
                label={field.label}
                name={field.name}
                onChange={props.handleAcquisitionDetailsOnChange}
                value={field.value}
                invalidInputMessage={
                  props.wayOfGettingProperty === "inheritance"
                    ? HAKLAI_PRE_CALC_ERRORS.impossibleDate
                    : HAKLAI_PRE_CALC_ERRORS.genericDateInvalid
                }
                validateInvalidInputOnBlur
                error={Boolean(fieldErrors[field.name])}
                helperText={fieldErrors[field.name] || HELPER_TEXT_PLACEHOLDER}
                helperTextMinHeight={FIELD_HELPER_SLOT_PX}
                helperTextSingleLine
                centerHelperText
                textFieldWidth={PRECALC_INPUT_WIDTHS.date}
                exactWidth
                stackLabelOnXs
                onBlur={() => handlePrerequisiteFieldBlur(field.name)}
              />
            </Grid>
          </React.Fragment>
        ))}
        {acquisitionDetailsConfig.showAmount ? renderAmountCurrencyFields(
          acquisitionDetailsConfig.amountLabel,
          acquisitionCurrencyOptions
        ) : null}
      </Grid>
    );
  };

  const renderRabinovichSection = () => {
    if (!shouldShowRabinovich) return null;
    return (
      <Box className={classes.rabinovichSection}>
        <Box className={classes.rabinovichRow}>
          <FormControlLabel
            className={classes.rabinovichLabel}
            labelPlacement="end"
            control={
              <Checkbox
                checked={props.acquisitionDetails?.hasRabinovich || false}
                value={!props.acquisitionDetails?.hasRabinovich}
                onChange={props.handleAcquisitionDetailsOnChange}
                name="hasRabinovich"
                color="primary"
              />
            }
            label={HAKLAI_PRE_CALC_LABELS.rabinovichEligibilityQuestion}
          />
          <CustomLink
            label={HAKLAI_PRE_CALC_LABELS.checkEligibility}
            onClick={(event) => {
              blurFocusedElement(event);
              setHasRabinovichDialogOpen(true);
            }}
          />
        </Box>
      </Box>
    );
  };

  const handleRabinovichResults = useCallback((e) => {
    handleAcquisitionDetailsOnChange(e);
    setHasRabinovichDialogOpen(false);
  }, [handleAcquisitionDetailsOnChange]);

  const renderWayOfGettingPropertySection = () => (
    <InlineQuestionField
      classes={classes}
      label={HAKLAI_PRE_CALC_LABELS.wayOfGettingProperty}
      helpDialog={haklaiHelpTexts.wayOfGettingProperty}
      hint={
        !props.wayOfGettingProperty && !fieldErrors.wayOfGettingProperty
          ? HAKLAI_PRE_CALC_ERRORS.wayOfGettingPropertyRequired
          : null
      }
      errorText={fieldErrors.wayOfGettingProperty || ""}
    >
      <RadioGroup
        row
        name="wayOfGettingProperty"
        value={props.wayOfGettingProperty || ""}
        onChange={(e) => {
          props.handlePreCalcOnChange(e);
          handlePrerequisiteFieldBlur("wayOfGettingProperty");
        }}
      >
        {WAY_OF_GETTING_PROPERTY_OPTIONS.map((opt) => (
          <FormControlLabel
            key={opt.value}
            value={opt.value}
            control={<Radio color="primary" size="small" />}
            label={opt.label}
            labelPlacement="end"
          />
        ))}
      </RadioGroup>
    </InlineQuestionField>
  );

  const basicInfoFirstRowFields = [
    {
      key: "soldPropertyDate",
      label: HAKLAI_PRE_CALC_LABELS.soldDate,
      labelWidth: PRECALC_LABEL_WIDTHS.date,
      alignLabelToInput: true,
      control: (
        <LabeledTextField
          type="date"
          name="soldPropertyDate"
          onChange={props.handlePreCalcOnChange}
          value={props.soldPropertyDate || ""}
          invalidInputMessage={HAKLAI_PRE_CALC_ERRORS.soldDateInvalid}
          validateInvalidInputOnBlur
          error={Boolean(fieldErrors.soldPropertyDate)}
          helperText={fieldErrors.soldPropertyDate || HELPER_TEXT_PLACEHOLDER}
          helperTextMinHeight={FIELD_HELPER_SLOT_PX}
          helperTextSingleLine
          centerHelperText
          textFieldWidth={PRECALC_INPUT_WIDTHS.date}
          exactWidth
          onBlur={() => handlePrerequisiteFieldBlur("soldPropertyDate")}
        />
      )
    },
    {
      key: "totalPropertyCost",
      label: HAKLAI_PRE_CALC_LABELS.saleValue,
      labelWidth: PRECALC_LABEL_WIDTHS.money,
      alignLabelToInput: true,
      control: (
        <LabeledTextField
          type="number"
          name="totalPropertyCost"
          textFieldWidth={PRECALC_INPUT_WIDTHS.money}
          max={MAX_MONEY_AMOUNT}
          onChange={props.handlePreCalcOnChange}
          value={props.totalPropertyCost || ""}
          currency="₪"
          error={Boolean(fieldErrors.totalPropertyCost)}
          helperText={fieldErrors.totalPropertyCost || HELPER_TEXT_PLACEHOLDER}
          helperTextMinHeight={FIELD_HELPER_SLOT_PX}
          centerHelperText
          onBlur={() => handlePrerequisiteFieldBlur("totalPropertyCost")}
        />
      )
    }
  ];

  const basicInfoFields = basicInfoFirstRowFields;

  return (
    <Box ref={sectionRootRef} className="haklai-pre-calc">
      <Grid className="haklai-calc-stack" container direction="column" spacing={0}>
        {/* Section 1: Basic Info */}
        <Grid item>
          <Box className={classes.section}>
            <Box className={classes.sectionHeader}>
              <ButtonBase
                className={classes.sectionHeaderButton}
                onClick={() => setBasicInfoExpanded((prev) => !prev)}
              >
                <Typography className={classes.sectionHeaderTitle}>
                  {HAKLAI_PRE_CALC_LABELS.basicInfoTitle}
                </Typography>
                <ExpandMoreIcon
                  className={[
                    classes.sectionToggleIcon,
                    basicInfoExpanded ? classes.sectionToggleIconExpanded : ""
                  ].filter(Boolean).join(" ")}
                />
              </ButtonBase>
            </Box>
            <Collapse in={basicInfoExpanded} timeout={PRE_CALC_SECTION_TRANSITION_MS} unmountOnExit={false}>
              <Box className={classes.sectionContent}>
                <Grid container direction="column" spacing={0}>
                  <Grid item className={classes.basicInfoFieldRow}>
                    <Grid container direction="row" spacing={1} alignItems="center" wrap="wrap">
                      {basicInfoFields.map((field) => (
                        <BasicInfoField
                          key={field.key}
                          classes={classes}
                          label={field.label}
                          helpDialog={field.helpDialog}
                          labelWidth={field.labelWidth}
                          alignLabelToInput={field.alignLabelToInput === true}
                          fieldClassName={field.fieldClassName}
                          belowMainRow={field.belowMainRow}
                        >
                          {field.control}
                        </BasicInfoField>
                      ))}
                    </Grid>
                  </Grid>
                </Grid>
              </Box>
            </Collapse>
          </Box>
        </Grid>

        {/* Section 2: Acquisition Details */}
        <Grid item>
          <Box className={classes.section}>
            <Box className={classes.sectionHeader}>
              <ButtonBase
                className={classes.sectionHeaderButton}
                onClick={() => setAcquisitionExpanded((prev) => !prev)}
              >
                <Typography className={classes.sectionHeaderTitle}>
                  {HAKLAI_PRE_CALC_LABELS.acquisitionDetailsTitle}
                </Typography>
                <ExpandMoreIcon
                  className={[
                    classes.sectionToggleIcon,
                    acquisitionExpanded ? classes.sectionToggleIconExpanded : ""
                  ].filter(Boolean).join(" ")}
                />
              </ButtonBase>
            </Box>
            <Collapse in={acquisitionExpanded} timeout={PRE_CALC_SECTION_TRANSITION_MS} unmountOnExit={false}>
              <Box
                className={`${classes.sectionContent} ${classes.sectionContentStack} ${classes.acquisitionSectionContent}`}
              >
                <Grid container direction="column" spacing={1} alignItems="flex-start">
                  <Grid item>
                    {renderWayOfGettingPropertySection()}
                  </Grid>
                  {props.wayOfGettingProperty && (
                    <Grid item className={classes.acquisitionDetailsBelow}>
                      <Box className={classes.acquisitionDetailsStack}>
                        {renderAcquisitionDetails()}
                      </Box>
                    </Grid>
                  )}
                </Grid>
                {renderRabinovichSection()}
              </Box>
            </Collapse>
          </Box>
        </Grid>
      </Grid>

      {/* HasRabinovichDialog */}
      <HasRabinovichDialog
        open={hasRabinovichDialogOpen}
        onClose={() => setHasRabinovichDialogOpen(false)}
        handleResults={handleRabinovichResults}
      />
    </Box>
  );
}));

HaklaiPreCalcSection.displayName = "HaklaiPreCalcSection";

export default HaklaiPreCalcSection;
